const REPO_OWNER = 'amitha511';
const REPO_NAME = 'noa_front';
const FILE_PATH = 'public/noaWebsite.products.json';
const API_BASE = 'https://api.github.com';

/**
 * Validate a GitHub PAT by checking user info + repo access.
 * Returns the GitHub username on success, or throws on failure.
 */
export const validateToken = async (token) => {
  const res = await fetch(`${API_BASE}/user`, {
    headers: {
      Authorization: `token ${token}`,
      Accept: 'application/vnd.github.v3+json',
    },
  });
  if (!res.ok) throw new Error('Invalid token');
  const user = await res.json();

  // Check repo access
  const repoRes = await fetch(`${API_BASE}/repos/${REPO_OWNER}/${REPO_NAME}`, {
    headers: {
      Authorization: `token ${token}`,
      Accept: 'application/vnd.github.v3+json',
    },
  });
  if (!repoRes.ok) throw new Error('No access to repository');

  return user.login;
};

/**
 * Fetch products directly from GitHub repo (always fresh, no cache).
 * Returns { products: Array, sha: string }
 */
export const fetchProductsFromGitHub = async (token) => {
  const url = `${API_BASE}/repos/${REPO_OWNER}/${REPO_NAME}/contents/${FILE_PATH}`;
  const res = await fetch(url, {
    headers: {
      Authorization: `token ${token}`,
      Accept: 'application/vnd.github.v3+json',
    },
  });
  if (!res.ok) {
    const errorBody = await res.json().catch(() => ({}));
    console.error('GitHub API error:', res.status, errorBody);
    throw new Error(
      `GitHub API error (${res.status}): ${errorBody.message || 'Unknown error'}`
    );
  }
  const data = await res.json();

  // Decode base64 content (supports UTF-8 / Hebrew characters)
  const binaryString = atob(data.content.replace(/\n/g, ''));
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  const content = new TextDecoder('utf-8').decode(bytes);
  const products = JSON.parse(content);

  return { products, sha: data.sha };
};

/**
 * Save products to the GitHub repo (creates a commit).
 * Returns the new sha.
 */
export const saveProductsToGitHub = async (token, products, sha) => {
  const jsonContent = JSON.stringify(products, null, 2) + '\n';
  const base64Content = btoa(unescape(encodeURIComponent(jsonContent)));

  const res = await fetch(
    `${API_BASE}/repos/${REPO_OWNER}/${REPO_NAME}/contents/${FILE_PATH}`,
    {
      method: 'PUT',
      headers: {
        Authorization: `token ${token}`,
        Accept: 'application/vnd.github.v3+json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message: 'Update products via Admin Dashboard',
        content: base64Content,
        sha: sha,
      }),
    }
  );

  if (!res.ok) {
    const errorData = await res.json();
    throw new Error(errorData.message || 'Failed to save products');
  }

  const result = await res.json();
  return result.content.sha;
};

/**
 * Reorder products so that starred items are at indices 0-7.
 * Non-starred items keep their relative order after the starred ones.
 */
export const reorderWithStars = (products, starredIds) => {
  if (!starredIds || starredIds.length === 0) return [...products];

  const starred = [];
  const unstarred = [];

  products.forEach((product) => {
    if (starredIds.includes(product._id)) {
      starred.push(product);
    } else {
      unstarred.push(product);
    }
  });

  // Maintain the starred order as the user selected them
  const orderedStarred = starredIds
    .map((id) => starred.find((p) => p._id === id))
    .filter(Boolean);

  return [...orderedStarred, ...unstarred];
};

/**
 * Generate a random hex ID matching the existing _id format (24-char hex string).
 */
export const generateId = () => {
  const chars = '0123456789abcdef';
  let id = '';
  for (let i = 0; i < 24; i++) {
    id += chars[Math.floor(Math.random() * chars.length)];
  }
  return id;
};
